import { useEffect, useRef, useState, type CSSProperties } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
// Os cards animados vivem num componente compartilhado: eles existiam duplicados
// aqui e no editor manual, e catalogo duplicado e como os dois lados comecam a
// oferecer estilos diferentes sem ninguem notar.
import {
  CaptionStyleCards, HeadlineStyleCards, CAPTION_DEMO_CSS,
  OverlayStyleCards, OverlayStyleHelp, type OverlayStyle,
} from './CaptionStyleCards';
import type { VideoEditorCaptionStyle, VideoEditorHeadlineStyle } from '../../types/videoEditor';
import { MarketingBrand } from '../../types';
import { PublishModal } from './AssetGallery';
import { composeCaption } from '../../utils/captionHashtags';
import { VideoProjectEditor } from './VideoProjectEditor';
import { EdvidEditor, type EdvidTab } from './edvid/EdvidEditor';
import type { EdvidJobState } from '../../types/videoEdit';
import { EditElementsPanel } from './EditElementsCards';
import { CreditCostNotice } from '../credits/CreditCostNotice';
import { useCreditConfirm } from '../credits/useCreditConfirm';
import { useCredits } from '../../hooks/useCredits';

// Cada edição custa uma cobrança só, independente de quantos vídeos entraram ou
// quantos cortes saíram. O preço de cada modo é editável no superadmin.
// Estilos de overlay oferecidos. Cada um e um DESENHO diferente de video, nao uma
// variacao de cor — por isso o seletor mostra uma miniatura de cada um em vez de
// um texto num <select>: a pessoa escolhe olhando.
//
// O catalogo, a miniatura e a grade MORAM em CaptionStyleCards.tsx. Estavam
// duplicados aqui e no passo a passo de producao (ViralResearchView), e a
// duplicacao ja tinha cobrado: a correcao do `flex flex-col` (desalinhamento de
// ~7px entre os rotulos, medido) foi aplicada so na copia de la e esta tela
// rodava desalinhada. As quatro divergencias e o lado escolhido em cada uma
// estao comentados no proprio catalogo.

// Dentro do modo "Apenas legenda" o render ainda tem DUAS decisoes separadas: como
// a legenda entra na tela e como o gancho dos primeiros segundos e desenhado. Os
// ids sao os mesmos do STYLE_CATALOG da referencia
// (edvid/assets/preview/app.js, ~157 e ~148), para o backend nao ter de traduzir.




// Espelho do teto do backend (VIDEO_EDITOR_MAX_FILE_MB em videoEditorController.ts).
const VIDEO_EDITOR_MAX_FILE_MB = 2048;

// Motor do editor. 'edvid' (padrão): corte → aprovação → Estilo → visual, e o
// passo a passo de criação fica só com a direção — overlay, legenda, elementos e
// música passam para a aba Estilo do editor. 'legacy' mantém o fluxo antigo.
// Jobs antigos (sem output.edvid) continuam abrindo o VideoProjectEditor.
const VIDEO_EDITOR_ENGINE = import.meta.env.VITE_VIDEO_EDITOR_ENGINE ?? 'edvid';

const EDVID_PHASE_LABEL: Record<EdvidJobState['phase'], string> = {
  cutting: 'Cortando o vídeo…',
  cut_ready: 'Corte pronto — escolha o estilo',
  visual_rendering: 'Gerando o visual…',
  done: 'Final pronto',
  failed: 'Falhou',
};

const EDIT_FEATURE_KEY: Record<'editor' | 'viral_cuts', string> = {
  editor: 'video_edit_ai',
  viral_cuts: 'video_viral_cuts',
};

interface Props {
  brand: MarketingBrand | null;
}

type JobStatus = 'queued' | 'running' | 'completed' | 'failed';

interface EditSegment {
  id: string;
  sourceVideoIndex: number;
  start: number;
  end: number;
  narrativeRole: string;
  reason: string;
  overlayHint?: string;
}

interface EditManifest {
  summary: string;
  narrativePlan: string;
  estimatedDuration: number;
  retentionHookOverlay?: { text: string; reason: string };
  segments: EditSegment[];
  discardedContent?: Array<{ sourceVideoIndex: number; approximateStart: number; approximateEnd: number; reason: string }>;
}

interface VideoEditorJob {
  id: string;
  status: JobStatus;
  currentStage: string;
  createdAt: string;
  completedAt?: string;
  input: {
    videoUrls?: string[];
    originalFilenames: string[];
    options: { addOverlays: boolean; smartReframe?: boolean; mode?: 'editor' | 'viral_cuts' };
    brandId?: string;
  };
  output: {
    transcripts?: Array<{ videoIndex: number; text: string; wordCount: number; filename: string }>;
    editManifest?: EditManifest;
    stats?: {
      totalInputVideos: number;
      totalOriginalDuration: number;
      finalDuration: number;
      savedSeconds: number;
      segmentsUsed: number;
    };
    finalVideoUrl?: string;
    hasFinalVideo?: boolean;
    viralClips?: Array<{
      id: string;
      index: number;
      url?: string;
      hasVideo?: boolean;
      title: string;
      reason: string;
      score: number;
      duration: number;
      sourceFilename?: string;
      start?: number;
      end?: number;
    }>;
    mode?: 'editor' | 'viral_cuts';
    // Motor edvid: estado das fases (corte / estilo / visual). Ver types/videoEdit.ts.
    edvid?: EdvidJobState;
    captionSeo?: any;
    // Trilha do Instagram escolhida pela IA (ou trocada no editor). Metadado de
    // publicacao — nao esta dentro do arquivo de video.
    musicSelection?: {
      audioId: string;
      title?: string | null;
      artist?: string | null;
      volume?: number;
      enabled?: boolean;
      pickedBy?: 'llm' | 'user';
      reason?: string | null;
      previewUrl?: string | null;
      // false = e ARQUIVO de audio (toca aqui). true/undefined = pagina do
      // instagram.com. A Meta devolve download_url null quando nao ha previa.
      previewIsExternal?: boolean;
      // Copia no nosso storage: quando existe, e a fonte preferida (nao expira).
      audioUrl?: string | null;
    } | null;
    musicUnavailable?: string | null;
    // Exportacoes do editor por target ('final' ou id do corte viral). Sem URL: o backend
    // remove antes de responder, e a midia e servida por artifact/URL assinada.
    editorRenders?: Record<string, {
      id: string;
      status: 'idle' | 'queued' | 'running' | 'completed' | 'failed';
      progress?: number;
      duration?: number;
      completedAt?: string;
      error?: string;
    } | null>;
    error?: string;
  };
}

// Decide pela URL, nao pela flag gravada: selecoes antigas vem com
// previewIsExternal errado (true por omissao) e o audio, que e arquivo, nunca
// tocava. A URL nao envelhece; a flag sim.
function isInstagramPageLink(url?: string | null): boolean {
  return !!url && /(^|\/\/)([\w-]+\.)*instagram\.com\//i.test(url);
}

function musicAudioSource(music?: { audioUrl?: string | null; previewUrl?: string | null } | null): string | null {
  if (music?.audioUrl) return music.audioUrl;
  if (music?.previewUrl && !isInstagramPageLink(music.previewUrl)) return music.previewUrl;
  return null;
}

const STAGE_LABELS: Record<string, string> = {
  queued: 'Na fila...',
  download: 'Baixando vídeos...',
  transcribe: 'Transcrevendo e detectando erros de áudio...',
  visual_analysis: 'ChatGPT analisando cenas e visual...',
  edit_plan: 'LLM criando plano de edição viral...',
  edit: 'Montando vídeo...',
  transcribe_final: 'Transcrevendo vídeo montado...',
  overlays: 'Aplicando overlays da brand...',
  caption: 'Gerando caption e hashtags...',
  music: 'IA escolhendo a trilha de fundo...',
  done: 'Concluído',
};

const ROLE_LABELS: Record<string, { label: string; color: string }> = {
  hook: { label: 'Hook', color: 'bg-red-100 text-red-700' },
  amplification: { label: 'Amplificação', color: 'bg-orange-100 text-orange-700' },
  body: { label: 'Corpo', color: 'bg-blue-100 text-blue-700' },
  cta: { label: 'CTA', color: 'bg-emerald-100 text-emerald-700' },
  transition: { label: 'Transição', color: 'bg-slate-100 text-slate-600' },
};

function formatDuration(seconds: number): string {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function ProgressBar({ stage }: { stage: string }) {
  const stages = ['download', 'transcribe', 'visual_analysis', 'edit_plan', 'edit', 'transcribe_final', 'overlays', 'caption', 'music', 'done'];
  const current = stages.indexOf(stage);
  const pct = current < 0 ? 5 : Math.round(((current + 1) / stages.length) * 100);
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{STAGE_LABELS[stage] || stage}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-indigo-500 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function VideoModal({ url, onClose, jobId, artifact = 'final', music }: {
  url?: string; onClose: () => void; jobId?: string; artifact?: string;
  // Trilha do Instagram escolhida para esta edicao. Toca JUNTO com o video, so
  // para quem esta assistindo: ela nao esta dentro do arquivo (e anexada na
  // publicacao), entao sem isso quem revisa aprova sem nunca ouvir a musica.
  music?: { audioId: string; audioUrl?: string | null; previewUrl?: string | null; previewIsExternal?: boolean; volume?: number; enabled?: boolean } | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const musicRef = useRef<HTMLAudioElement>(null);
  // Marcar e reclamar sem timeline: quem aprova o vídeo assiste e aponta o
  // trecho pelo próprio player. A marcação vai para o mesmo lugar das feitas na
  // régua do editor — muda a porta de entrada, não a estrutura.
  const [markStart, setMarkStart] = useState<number | null>(null);
  const [draft, setDraft] = useState<{ start: number; end: number; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !draft) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [draft, onClose]);

  // Segue o transporte do video: play, pause, seek e fim. Trilha de fundo comeca
  // em 0 e repete (`loop`), porque a previa costuma ser mais curta que o video.
  // Decide pela URL, nao pela flag: selecoes antigas vem com previewIsExternal
  // errado e o audio — que e arquivo — nunca tocava.
  const musicSrc = musicAudioSource(music);
  // A caixinha e SO de previa: ligar ou desligar aqui nao muda o que vai para o
  // Instagram. Serve para ouvir como ficou com e sem trilha.
  const [previewWithMusic, setPreviewWithMusic] = useState(true);
  const musicPlayable = Boolean(musicSrc && previewWithMusic);
  useEffect(() => {
    const video = videoRef.current;
    const audio = musicRef.current;
    if (!video || !audio || !musicPlayable) return;

    audio.volume = Math.min(1, Math.max(0, music?.volume ?? 0.1));
    const sync = () => {
      const dur = audio.duration;
      audio.currentTime = dur && Number.isFinite(dur) && dur > 0 ? video.currentTime % dur : video.currentTime;
    };
    const onPlay = () => { sync(); void audio.play().catch(() => {}); };
    const onPause = () => audio.pause();

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onPause);
    video.addEventListener('seeked', sync);
    if (!video.paused) onPlay();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onPause);
      video.removeEventListener('seeked', sync);
      audio.pause();
    };
  }, [musicPlayable, musicSrc, music?.volume]);

  const toggleMark = () => {
    const at = videoRef.current?.currentTime ?? 0;
    if (markStart === null) { setMarkStart(at); return; }
    const start = Math.min(markStart, at);
    const end = Math.max(markStart, at);
    setMarkStart(null);
    setDraft({ start, end: Math.max(end, start + 0.05), text: '' });
  };

  const saveNote = async () => {
    if (!draft || !jobId || saving) return;
    const text = draft.text.trim();
    if (!text) { setDraft(null); return; }
    setSaving(true);
    try {
      // Recarrega o projeto na hora de salvar: entre abrir o player e escrever a
      // marcação, alguém pode ter mexido no projeto.
      //
      // `artifact` identifica a MÍDIA, `target` identifica o PROJETO — e depois de
      // uma exportação a mídia vira "editor:final", que não é um target válido.
      // Sem esta tradução a marcação morre exatamente no caso mais comum: quem já
      // exportou uma vez e volta ao preview para revisar.
      const target = artifact.startsWith('editor:')
        ? (artifact.slice('editor:'.length) || 'final')
        : artifact;
      const current = await api.getVideoEditorProject(jobId, target);
      const project = current.project;
      const updated = {
        ...project,
        notes: [
          ...(project.notes || []),
          {
            id: `note_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
            start: +draft.start.toFixed(3),
            end: +draft.end.toFixed(3),
            text,
            status: 'open' as const,
            createdAt: new Date().toISOString(),
            origin: 'preview' as const,
          },
        ],
      };
      await api.saveVideoEditorProject(jobId, target, updated, project.revision);
      setSavedCount(count => count + 1);
      setDraft(null);
      toast.success('Marcação salva. Ela aparece no editor, no trecho exato.');
    } catch (error: any) {
      toast.error(error?.message || 'Não foi possível salvar a marcação.');
    } finally {
      setSaving(false);
    }
  };

  const stamp = (seconds: number) => {
    const total = Math.max(0, Math.floor(seconds));
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={e => { if (e.target === e.currentTarget && !draft) onClose(); }}
    >
      <div ref={ref} className="relative w-full max-w-sm">
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm font-bold px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20"
        >
          ✕ Fechar
        </button>
        {/* Linha unica, na largura do video e logo acima dele. O bloco anterior
            ocupava tres linhas e roubava a atencao de quem veio assistir. O
            "so aqui" vai no title, nao em paragrafo. */}
        {musicSrc && (
          <label
            className="mb-1.5 flex w-full cursor-pointer items-center gap-1.5 px-1 text-[11px] text-emerald-300/80 hover:text-emerald-200"
            title="Afeta apenas esta reprodução — desmarcar não remove a música da publicação."
          >
            {previewWithMusic && (
              // metadata, nao auto: baixar a faixa inteira junto com o video
              // disputa banda e trava a reproducao. Os bytes vem no play.
              <audio ref={musicRef} src={musicSrc} loop preload="metadata" className="hidden" />
            )}
            <input
              type="checkbox"
              checked={previewWithMusic}
              onChange={e => setPreviewWithMusic(e.target.checked)}
              className="h-3 w-3 shrink-0 rounded"
            />
            <span className="truncate">♫ Ouvir com música ({Math.round((music?.volume ?? 0.1) * 100)}%)</span>
          </label>
        )}

        {jobId ? (
          <AuthVideo videoRef={videoRef} jobId={jobId} artifact={artifact} autoPlay className="w-full rounded-2xl shadow-2xl" style={{ maxHeight: '80vh' }} />
        ) : (
          <video ref={videoRef} src={url} controls autoPlay className="w-full rounded-2xl shadow-2xl" style={{ maxHeight: '80vh' }} />
        )}

        {jobId && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border border-white/10 bg-slate-900/80 px-3 py-2">
            <button
              onClick={toggleMark}
              className={`rounded-lg px-3 py-1.5 text-xs font-black ${markStart === null ? 'bg-amber-400/20 text-amber-200 hover:bg-amber-400/30' : 'bg-amber-400 text-slate-950'}`}
            >
              {markStart === null ? '⚑ Marcar problema' : `⚑ Fechar em ${stamp(videoRef.current?.currentTime ?? 0)}`}
            </button>
            <p className="text-[10px] leading-tight text-slate-400">
              {markStart === null
                ? 'Pare onde está ruim e marque. Não precisa saber editar.'
                : `Início marcado em ${stamp(markStart)} — vá até o fim do problema e feche.`}
            </p>
            {savedCount > 0 && (
              <span className="ml-auto shrink-0 rounded-lg bg-emerald-500/15 px-2 py-1 text-[10px] font-black text-emerald-300">
                {savedCount} salva{savedCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {draft && (
          <div className="absolute inset-x-0 bottom-0 z-10 rounded-2xl border border-amber-300/30 bg-slate-900 p-4 shadow-2xl">
            <p className="text-[10px] font-black uppercase tracking-wider text-amber-300/80">
              Marcação · {stamp(draft.start)} – {stamp(draft.end)}
            </p>
            <textarea
              autoFocus
              value={draft.text}
              onChange={e => setDraft(current => current && { ...current, text: e.target.value })}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void saveNote(); }
                if (e.key === 'Escape') { e.preventDefault(); setDraft(null); }
              }}
              rows={2}
              maxLength={1000}
              placeholder="ex: aqui ficou arrastado, corta essa parte"
              className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-slate-950 p-2.5 text-sm text-slate-100 outline-none focus:border-amber-300/50"
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              <button onClick={() => setDraft(null)} className="rounded-lg px-3 py-1.5 text-xs font-black text-slate-400 hover:bg-white/10">Descartar</button>
              <button onClick={() => void saveNote()} disabled={saving} className="rounded-lg bg-amber-400 px-3 py-1.5 text-xs font-black text-slate-950 hover:bg-amber-300 disabled:opacity-60">
                {saving ? 'Salvando…' : 'Salvar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AuthVideo({ jobId, artifact, className, style, controls = true, autoPlay = false, muted = false, preload, onLoadedData, videoRef }: {
  jobId: string;
  artifact: string;
  className?: string;
  style?: CSSProperties;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  preload?: string;
  onLoadedData?: () => void;
  videoRef?: React.RefObject<HTMLVideoElement>;
}) {
  const [src, setSrc] = useState<string>('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const retryRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: number | undefined;
    setError('');
    // artifact → id da midia: 'final' (corte da IA), 'editor:<target>' (edicao exportada,
    // que no catalogo se chama 'edited-<target>') ou id de corte viral.
    const assetId = artifact === 'final'
      ? 'final'
      : artifact.startsWith('editor:')
        ? `edited-${artifact.slice('editor:'.length) || 'final'}`
        : `viral-${artifact}`;
    api.getVideoEditorAssetUrl(jobId, assetId)
      .then(response => {
        if (cancelled) return;
        setSrc(response.asset.url);
        const expiresAt = Date.parse(response.asset.expiresAt || '');
        if (Number.isFinite(expiresAt)) {
          refreshTimer = window.setTimeout(
            () => setRefreshKey(value => value + 1),
            Math.max(5_000, expiresAt - Date.now() - 60_000),
          );
        }
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Erro ao carregar vídeo.');
      });
    return () => {
      cancelled = true;
      if (refreshTimer) window.clearTimeout(refreshTimer);
    };
  }, [jobId, artifact, refreshKey]);

  if (error) {
    return (
      <div className={`${className || ''} flex items-center justify-center bg-slate-900 p-4 text-center text-xs text-slate-300`} style={style}>
        {error}
      </div>
    );
  }

  if (!src) {
    return (
      <div className={`${className || ''} flex items-center justify-center bg-slate-900 text-xs text-slate-400`} style={style}>
        Carregando vídeo...
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={src}
      controls={controls}
      autoPlay={autoPlay}
      muted={muted}
      preload={preload}
      onLoadedData={() => { retryRef.current = 0; onLoadedData?.(); }}
      onError={() => {
        if (retryRef.current >= 2) return setError('Não foi possível carregar o vídeo.');
        retryRef.current += 1;
        setRefreshKey(value => value + 1);
      }}
      className={className}
      style={style}
    />
  );
}

function VideoThumbnail({ jobId, artifact = 'final', onClick }: { jobId: string; artifact?: string; onClick: () => void }) {
  const [thumbReady, setThumbReady] = useState(false);

  return (
    <div
      onClick={onClick}
      className="relative w-28 shrink-0 cursor-pointer rounded-xl overflow-hidden border border-slate-700 bg-slate-900 group"
      style={{ aspectRatio: '9/16' }}
    >
      <AuthVideo
        jobId={jobId}
        artifact={artifact}
        controls={false}
        muted
        preload="metadata"
        className="absolute inset-0 h-full w-full object-cover"
        onLoadedData={() => setThumbReady(true)}
      />
      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-lg group-hover:scale-110 transition-transform">
          <span className="text-slate-900 text-lg ml-0.5">▶</span>
        </div>
      </div>
      {!thumbReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
          <span className="text-2xl">🎬</span>
        </div>
      )}
    </div>
  );
}

function JobCard({ job, onDelete, onOpenEditor, onOpenEdvid }: {
  job: VideoEditorJob;
  onDelete: () => void;
  onOpenEditor: (target: string, title: string) => void;
  onOpenEdvid: (tab: EdvidTab, title: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [originalModalOpen, setOriginalModalOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishAssetUrl, setPublishAssetUrl] = useState('');
  const out = job.output || {};
  const manifest = out.editManifest;
  const stats = out.stats;
  const captionSeo = out.captionSeo || null;
  // Mesma divisão legenda × hashtags da publicação (ver utils/captionHashtags):
  // caption com o bloco de tags no fim aparecia e publicava duplicado.
  const captionParts = composeCaption(captionSeo?.caption || '', captionSeo?.hashtagsFlat || '');
  const viralClips = out.viralClips || [];
  const firstViralClip = viralClips[0] || null;
  const isViralCuts = (out.mode || job.input?.options?.mode) === 'viral_cuts';
  const edvid = out.edvid || null;
  const edvidBusy = edvid?.phase === 'cutting' || edvid?.phase === 'visual_rendering';
  const isRunning = job.status === 'running' || job.status === 'queued';
  const isFailed = job.status === 'failed';
  // No motor edvid o job entrega por fase: com o corte pronto (e nada renderizando)
  // já existe um MP4 para tocar, baixar e publicar — output.finalVideoUrl aponta
  // sempre para o entregável mais recente (corte, depois o final).
  const isDone = job.status === 'completed' || (!!edvid?.cut && !edvidBusy && edvid.phase !== 'failed');
  const hasFinalVideo = Boolean(out.hasFinalVideo || out.finalVideoUrl);
  // Trilha desligada no editor continua gravada, mas nao vai para a publicacao —
  // entao o card tambem nao deve anuncia-la.
  const musicSelection = out.musicSelection?.enabled === false ? null : out.musicSelection;
  // Volume ajustavel direto no card. Estado local para o slider responder na hora
  // (e para a reproducao seguir junto) enquanto a gravacao vai em segundo plano.
  const [musicVolumeLocal, setMusicVolumeLocal] = useState<number | null>(null);
  const musicVolumeEffective = musicVolumeLocal ?? musicSelection?.volume ?? 0.1;
  const musicSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistMusicVolume = (value: number) => {
    setMusicVolumeLocal(value);
    if (musicSaveTimer.current) clearTimeout(musicSaveTimer.current);
    // Arrastar o slider dispara dezenas de eventos: so grava quando parar.
    musicSaveTimer.current = setTimeout(() => {
      api.updateVideoEditorMusic(job.id, { volume: value })
        .catch((e: any) => toast.error(e?.message || 'Nao foi possivel salvar o volume da trilha.'));
    }, 500);
  };
  const filenames = job.input?.originalFilenames || [];

  // Edicao exportada no editor: quando existe, ela e a versao "oficial" desta producao —
  // e ela que o card toca, publica e baixa. O corte da IA continua acessivel em Detalhes,
  // porque e dele que o editor parte (usar a versao editada como base queimaria overlay
  // sobre overlay na proxima exportacao).
  const editedRender = (target: string) => {
    const render = out.editorRenders?.[target];
    return render && render.status === 'completed' ? render : null;
  };
  const finalEdited = editedRender('final');
  const finalArtifact = finalEdited ? 'editor:final' : 'final';
  const finalExportingProgress = (() => {
    const render = out.editorRenders?.final;
    return render && (render.status === 'queued' || render.status === 'running') ? (render.progress || 0) : null;
  })();

  const downloadArtifact = async (artifact: string, filename: string) => {
    try {
      const blob = await api.downloadVideoEditorArtifact(job.id, artifact);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao baixar video.');
    }
  };

  // Asset sintético para o PublishModal
  const videoAsset = publishAssetUrl ? {
    id: job.id,
    type: 'video_editor',
    url: publishAssetUrl,
    previewUrl: publishAssetUrl,
    caption: captionParts.body,
    status: 'draft',
    title: filenames.length === 1 ? filenames[0] : `${filenames.length} vídeos editados`,
  } : null;

  const openPublish = async () => {
    try {
      // Publica a edicao exportada quando ela existe; senao, o corte da IA.
      const response = await api.getVideoEditorAssetUrl(job.id, finalEdited ? 'edited-final' : 'final', 'publish');
      setPublishAssetUrl(response.asset.url);
      setPublishOpen(true);
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao preparar o vídeo para publicação.');
    }
  };

  return (
    <>
      {modalOpen && hasFinalVideo && !isViralCuts && (
        <VideoModal
          jobId={job.id}
          artifact={finalArtifact}
          // Volume do slider, nao o gravado: mexer no card reflete na hora no player.
          music={musicSelection ? { ...musicSelection, volume: musicVolumeEffective } : null}
          onClose={() => setModalOpen(false)}
        />
      )}
      {originalModalOpen && hasFinalVideo && !isViralCuts && (
        <VideoModal jobId={job.id} artifact="final" onClose={() => setOriginalModalOpen(false)} />
      )}
      {publishOpen && videoAsset && (
        <PublishModal
          asset={videoAsset}
          allAssets={[videoAsset]}
          captionSeo={captionSeo}
          onClose={() => setPublishOpen(false)}
          onPublished={() => { setPublishOpen(false); toast.success('Publicado com sucesso!'); }}
          music={out.musicSelection || null}
          // Video do cliente editado, nao gerado: nao marca como IA por padrao.
          aiGeneratedDefault={false}
        />
      )}

    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-start gap-4 p-5">

        {/* Thumbnail clicável quando pronto, ícone de status quando não */}
        {isDone && isViralCuts && firstViralClip ? (
          <div className="w-28 shrink-0 overflow-hidden rounded-xl border border-slate-700 bg-slate-900" style={{ aspectRatio: '9/16' }}>
            <AuthVideo jobId={job.id} artifact={editedRender(firstViralClip.id) ? `editor:${firstViralClip.id}` : firstViralClip.id} controls={false} muted preload="metadata" className="h-full w-full object-cover" />
          </div>
        ) : isDone && hasFinalVideo && !isViralCuts ? (
          <VideoThumbnail jobId={job.id} artifact={finalArtifact} onClick={() => setModalOpen(true)} />
        ) : (
          <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-bold ${
            isFailed ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600 animate-pulse'
          }`}>
            {isFailed ? '✕' : '⟳'}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className="font-semibold text-slate-800">
            {isViralCuts ? 'Cortes virais IA' : filenames.length === 1 ? filenames[0] : `${filenames.length} vídeos`}
          </p>
          {filenames.length > 1 && (
            <p className="text-xs text-slate-400 truncate">{filenames.join(' · ')}</p>
          )}

          {/* Status badges */}
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
              isDone ? 'bg-emerald-100 text-emerald-700' :
              isFailed ? 'bg-red-100 text-red-700' :
              'bg-indigo-100 text-indigo-700'
            }`}>
              {edvid ? (edvid.phase === 'failed' ? 'Falhou' : EDVID_PHASE_LABEL[edvid.phase]) : isDone ? 'Concluído' : isFailed ? 'Falhou' : STAGE_LABELS[job.currentStage] || 'Processando...'}
            </span>

            {isDone && stats && (
              <>
                <span className="text-xs text-slate-500">
                  {isViralCuts && viralClips.length > 0
                    ? `${viralClips.length} cortes gerados`
                    : `${stats.totalInputVideos} vídeo${stats.totalInputVideos !== 1 ? 's' : ''} → ${formatDuration(stats.finalDuration)} final`}
                </span>
                {!isViralCuts && <span className="text-xs text-slate-400">-{formatDuration(stats.savedSeconds)} cortados</span>}
              </>
            )}

            {finalEdited && (
              <span className="rounded-full bg-fuchsia-100 px-2.5 py-0.5 text-xs font-bold text-fuchsia-700" title="A versão que você exportou no editor substituiu o corte da IA aqui: é ela que toca, baixa e publica.">
                ✎ Edição exportada
              </span>
            )}
            {finalExportingProgress !== null && (
              <span className="rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-bold text-indigo-700">
                Exportando edição {finalExportingProgress}%
              </span>
            )}
          </div>

          {/* Trilha escolhida pela IA. Aparece aqui, no card, porque ate agora ela
              so existia dentro do editor e no modal de publicacao — quem so olha o
              resultado publicava sem nunca saber que musica ia junto. */}
          {isDone && musicSelection?.audioId && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
              <span className="text-sm leading-none">♫</span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-emerald-900">
                  {musicSelection.title || musicSelection.audioId}
                  {musicSelection.artist ? <span className="font-normal text-emerald-700"> — {musicSelection.artist}</span> : null}
                </p>
                <p className="mt-0.5 text-[11px] text-slate-500">
                  Anexada ao Reel na publicação. O arquivo baixado não leva a música.
                </p>
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Volume</span>
                  <input
                    type="range" min={1} max={100}
                    value={Math.round(musicVolumeEffective * 100)}
                    onChange={e => persistMusicVolume(Number(e.target.value) / 100)}
                    className="h-1 flex-1 accent-emerald-600"
                  />
                  <span className="w-9 shrink-0 text-right text-[10px] font-mono text-slate-600">
                    {Math.round(musicVolumeEffective * 100)}%
                  </span>
                </div>
                {musicSelection.reason && (
                  <p className="mt-0.5 text-[11px] italic text-slate-500">{musicSelection.reason}</p>
                )}
              </div>
              {(musicAudioSource(musicSelection) || musicSelection.previewUrl) && (
                musicAudioSource(musicSelection) ? (
                  // Arquivo de audio de verdade: toca no proprio card, sem sair
                  // da tela. Antes o link caia num download, o que so confundia.
                  <audio
                    src={musicAudioSource(musicSelection) || undefined}
                    controls
                    preload="none"
                    className="h-8 w-44 shrink-0"
                    onLoadedMetadata={e => { (e.currentTarget as HTMLAudioElement).volume = musicVolumeEffective; }}
                  />
                ) : (
                  <a
                    href={musicSelection.previewUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-lg border border-emerald-300 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
                    title="Ouvir no Instagram (abre em nova aba)"
                  >
                    Ouvir ↗
                  </a>
                )
              )}
            </div>
          )}

          {/* Sem trilha: o motivo vale mais que o silencio. */}
          {isDone && !musicSelection?.audioId && out.musicUnavailable && (
            <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800">
              Sem música de fundo: {out.musicUnavailable}
            </p>
          )}

          {/* Progress bar quando rodando */}
          {isRunning && !edvid && <ProgressBar stage={job.currentStage} />}
          {edvid && (edvidBusy || edvid.phase === 'failed') && (
            <p className={`mt-2 text-xs ${edvid.phase === 'failed' ? 'text-red-600' : 'text-indigo-600'}`}>
              {edvid.phase === 'failed' ? (edvid.error || 'Erro técnico no processamento.') : (edvid.message || EDVID_PHASE_LABEL[edvid.phase])}
            </p>
          )}

          {/* Hint de clique no thumbnail */}
          {isDone && isViralCuts && firstViralClip && (
            <button
              onClick={() => setExpanded(true)}
              className="mt-2 text-xs text-indigo-500 hover:text-indigo-700 font-medium"
            >
              Ver cortes gerados
            </button>
          )}

          {isDone && hasFinalVideo && !isViralCuts && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                onClick={() => setModalOpen(true)}
                className="text-xs text-indigo-500 hover:text-indigo-700 font-medium"
              >
                ▶ {finalEdited ? 'Assistir edição' : 'Assistir vídeo'}
              </button>
              {finalEdited && (
                <button
                  onClick={() => setOriginalModalOpen(true)}
                  className="text-xs text-slate-400 hover:text-slate-600 font-medium"
                >
                  Ver corte original da IA
                </button>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {edvid?.cut && (
            // Ações por fase do motor edvid: o editor abre direto na aba certa.
            <>
              <button
                type="button"
                onClick={() => onOpenEdvid('cut', filenames[0] || 'Edição principal')}
                className={`rounded-xl px-4 py-2 text-xs font-bold ${edvid.visual ? 'border border-slate-200 text-slate-600 hover:bg-slate-50' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
              >
                Abrir corte
              </button>
              {!edvid.visual && (
                <button
                  type="button"
                  onClick={() => onOpenEdvid('style', filenames[0] || 'Edição principal')}
                  className="rounded-xl bg-fuchsia-600 px-4 py-2 text-xs font-bold text-white hover:bg-fuchsia-500"
                >
                  {edvid.phase === 'visual_rendering' ? 'Ver estilo' : 'Escolher estilo'}
                </button>
              )}
              {edvid.visual && (
                <button
                  type="button"
                  onClick={() => onOpenEdvid('final', filenames[0] || 'Edição principal')}
                  className="rounded-xl bg-fuchsia-600 px-4 py-2 text-xs font-bold text-white hover:bg-fuchsia-500"
                >
                  Abrir final
                </button>
              )}
            </>
          )}
          {isDone && hasFinalVideo && !isViralCuts && (
            <>
              {!edvid && (
                <button
                  type="button"
                  onClick={() => onOpenEditor('final', filenames[0] || 'Edição principal')}
                  className="rounded-xl bg-fuchsia-600 px-4 py-2 text-xs font-bold text-white hover:bg-fuchsia-500"
                >
                  Abrir no editor
                </button>
              )}
              <button
                onClick={() => void openPublish()}
                disabled={edvidBusy}
                title={edvidBusy ? 'Aguarde o render terminar' : undefined}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                Publicar
              </button>
              <button
                type="button"
                onClick={() => downloadArtifact(finalArtifact, 'video-editado.mp4')}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700"
              >
                Baixar
              </button>
            </>
          )}
          {isDone && hasFinalVideo && isViralCuts && (
            <>
              <button
                type="button"
                onClick={() => onOpenEditor('final', 'Compilado de cortes virais')}
                className="rounded-xl bg-fuchsia-600 px-4 py-2 text-xs font-bold text-white hover:bg-fuchsia-500"
              >
                Editar compilado
              </button>
              <button
                type="button"
                onClick={() => downloadArtifact(finalArtifact, 'cortes-virais-compilado.mp4')}
                className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700"
              >
                Baixar compilado
              </button>
            </>
          )}
          <button onClick={() => setExpanded(v => !v)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50">
            {expanded ? 'Fechar' : 'Detalhes'}
          </button>
          <button onClick={onDelete} className="rounded-xl border border-rose-200 px-3 py-2 text-xs text-rose-500 hover:bg-rose-50">
            ✕
          </button>
        </div>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 divide-y divide-slate-100">

          {isFailed && out.error && (
            <div className="p-5">
              <p className="text-sm font-semibold text-red-700 mb-1">Erro</p>
              <p className="text-xs text-red-600 font-mono break-all">{out.error}</p>
            </div>
          )}

          {isViralCuts && viralClips.length > 0 && (
            <div className="p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Cortes virais gerados</p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {viralClips.map(clip => {
                  // Corte que já foi exportado no editor: o card passa a mostrar e baixar a edição.
                  const clipEdited = editedRender(clip.id);
                  const clipArtifact = clipEdited ? `editor:${clip.id}` : clip.id;
                  return (
                  <div key={clip.id} className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                    <AuthVideo jobId={job.id} artifact={clipArtifact} className="w-full rounded-xl bg-slate-900" style={{ aspectRatio: '9/16', maxHeight: 360 }} />
                    <div className="mt-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-800">{clip.title}</p>
                        <p className="mt-1 text-xs text-slate-500">{clip.sourceFilename} · {formatDuration(clip.duration)}</p>
                      </div>
                      <span className="shrink-0 rounded-lg bg-emerald-100 px-2 py-1 text-xs font-black text-emerald-700">{clip.score}</span>
                    </div>
                    {clipEdited && (
                      <span className="mt-2 inline-block rounded-full bg-fuchsia-100 px-2 py-0.5 text-[11px] font-bold text-fuchsia-700">✎ Edição exportada</span>
                    )}
                    <p className="mt-2 line-clamp-3 text-xs text-slate-500">{clip.reason}</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => onOpenEditor(clip.id, clip.title || `Corte viral ${clip.index}`)} className="rounded-xl bg-fuchsia-600 px-3 py-2 text-center text-xs font-bold text-white hover:bg-fuchsia-500">
                        Editar corte
                      </button>
                      <button type="button" onClick={() => downloadArtifact(clipArtifact, `corte-viral-${String(clip.index).padStart(2, '0')}.mp4`)} className="rounded-xl bg-slate-900 px-3 py-2 text-center text-xs font-bold text-white hover:bg-slate-800">
                        Baixar
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Narrative plan */}
          {manifest && (
            <div className="p-5 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Plano narrativo da IA</p>
              <p className="text-sm text-slate-700">{manifest.summary}</p>
              {manifest.narrativePlan && (
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3">
                  <p className="text-xs font-semibold text-indigo-700 mb-1">Estrutura</p>
                  <p className="text-xs text-indigo-600">{manifest.narrativePlan}</p>
                </div>
              )}
              {manifest.retentionHookOverlay && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
                  <p className="text-xs font-semibold text-amber-700 mb-1">Hook de retenção (overlay)</p>
                  <p className="text-sm font-bold text-amber-800">"{manifest.retentionHookOverlay.text}"</p>
                  <p className="text-xs text-amber-600 mt-1">{manifest.retentionHookOverlay.reason}</p>
                </div>
              )}
            </div>
          )}

          {/* Segments */}
          {manifest?.segments && manifest.segments.length > 0 && (
            <div className="p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">
                Segmentos usados ({manifest.segments.length})
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {manifest.segments.map((seg, i) => {
                  const role = ROLE_LABELS[seg.narrativeRole] || { label: seg.narrativeRole, color: 'bg-slate-100 text-slate-600' };
                  const filename = filenames[seg.sourceVideoIndex] || `Vídeo ${seg.sourceVideoIndex + 1}`;
                  return (
                    <div key={seg.id} className="flex items-start gap-3 rounded-xl bg-white border border-slate-100 p-3">
                      <span className="shrink-0 w-6 text-center text-xs font-bold text-slate-400">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${role.color}`}>{role.label}</span>
                          <span className="text-xs text-slate-500 truncate">{filename}</span>
                          <span className="text-xs text-slate-400">{seg.start.toFixed(1)}s → {seg.end.toFixed(1)}s ({(seg.end - seg.start).toFixed(1)}s)</span>
                        </div>
                        <p className="text-xs text-slate-600">{seg.reason}</p>
                        {seg.overlayHint && (
                          <p className="text-xs text-fuchsia-600 mt-0.5 font-medium">Overlay: "{seg.overlayHint}"</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Discarded */}
          {manifest?.discardedContent && manifest.discardedContent.length > 0 && (
            <div className="p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                Conteúdo descartado ({manifest.discardedContent.length})
              </p>
              <div className="space-y-1.5">
                {manifest.discardedContent.map((d, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-slate-500">
                    <span className="shrink-0 text-rose-400">✕</span>
                    <span>{filenames[d.sourceVideoIndex] || `Vídeo ${d.sourceVideoIndex + 1}`} {d.approximateStart?.toFixed(1)}s–{d.approximateEnd?.toFixed(1)}s: {d.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transcripts */}
          {out.transcripts && out.transcripts.length > 0 && (
            <div className="p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Transcrições</p>
              <div className="space-y-3">
                {out.transcripts.map(t => (
                  <div key={t.videoIndex} className="rounded-xl bg-white border border-slate-100 p-3">
                    <p className="text-xs font-semibold text-slate-600 mb-1">{t.filename} · {t.wordCount} palavras</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{t.text?.slice(0, 300)}{(t.text?.length || 0) > 300 ? '...' : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Caption gerada */}
          {captionSeo?.caption && (
            <div className="p-5 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Caption gerada</p>
              <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-3">
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{captionParts.body}</p>
                {captionParts.tagLine && (
                  <p className="text-xs text-indigo-500">{captionParts.tagLine}</p>
                )}
                {captionSeo.automationSpec && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                    <p className="text-xs font-bold text-amber-700 mb-1">⚡ Automação detectada</p>
                    <p className="text-xs text-amber-600">
                      Keywords: {(captionSeo.automationSpec.keywords || []).join(', ')}
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      DM: {captionSeo.automationSpec.dmMessage?.slice(0, 80)}...
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={() => void openPublish()}
                disabled={edvidBusy}
                className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                Publicar ou agendar este vídeo
              </button>
            </div>
          )}

          {/* Botão de assistir no painel expandido */}
          {hasFinalVideo && !isViralCuts && (
            <div className="p-5">
              <button
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-3 rounded-2xl border border-indigo-200 bg-indigo-50 px-5 py-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-600 text-white text-base">▶</span>
                Assistir vídeo final
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}

// Miniatura dos tres estilos de legenda SEM animacao. As duas cues ocupam o mesmo
// lugar e uma troca pela outra sem transicao nenhuma. Parece bobo desenhar uma
// troca para dizer "nao tem animacao", mas e o unico jeito de a pessoa comparar:
// ao lado do karaoke, o pulo seco le como decisao, e um quadro parado leria como
// preview quebrado. A ~22% da borda de baixo porque o render poe a legenda a 430px
// da base num quadro de 1920 (430/1920 = 0,224).

export function VideoEditorView({ brand }: Props) {
  const [activeTab, setActiveTab] = useState<'editor' | 'viral_cuts'>('editor');
  const [jobs, setJobs] = useState<VideoEditorJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  // Progresso do envio em série: qual arquivo está subindo e quanto já foi.
  const [uploadProgress, setUploadProgress] = useState<{ index: number; fraction: number } | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [idea, setIdea] = useState('');
  const [clipCount, setClipCount] = useState(5);
  // 0 = automático (o backend calcula proporcional ao material enviado).
  const [targetDuration, setTargetDuration] = useState(0);
  // Estilo do overlay de abertura. 'hook' é o gancho tipográfico inspirado na
  // marca (o comportamento de sempre); 'question_box' troca todos os overlays
  // por uma caixinha de pergunta que fica o vídeo inteiro.
  const [overlayStyle, setOverlayStyle] = useState<OverlayStyle>('hook');
  // Sem marca cadastrada o 'hook' fica desabilitado, e deixar o estado nele
  // mandaria `overlayStyle=hook` para um backend que não tem tipografia para
  // aplicar — o vídeo sairia sem overlay nenhum sem nada na tela explicando.
  useEffect(() => {
    if (!brand && overlayStyle === 'hook') setOverlayStyle('caption');
  }, [brand, overlayStyle]);
  // Sub-escolhas do modo legenda. Os padroes sao os primeiros da lista da
  // referencia (defaultStyle() pega captions[0] e headlines[0]): karaoke e o
  // movimento que todo mundo reconhece de Reels, contorno e o gancho que funciona
  // sobre qualquer imagem porque nao depende de caixa nem de cor da marca.
  const [captionStyle, setCaptionStyle] = useState<VideoEditorCaptionStyle>('karaoke');
  const [headlineStyle, setHeadlineStyle] = useState<VideoEditorHeadlineStyle>('outline');
  // Cor de destaque dos estilos 'realce' e 'misto'. Sai de visualTokens.colors, o
  // mesmo campo que o BrandEditDrawer grava; sem marca cai no #ff5200, que e o
  // ACCENT_DEFAULT da referencia (app.js ~1085).
  const accentColor: string =
    ((brand?.visualTokens as any)?.colors?.accent as string | undefined)
    || ((brand?.visualTokens as any)?.colors?.primary as string | undefined)
    || '#ff5200';
  // Elementos de edição. Ligados por padrão: são o que separa um corte editado de
  // uma tomada picada. Ficam desligáveis porque material já editado (screencast,
  // gameplay) não quer flash nem zoom.
  // O zoom sao DOIS elementos independentes, nao um. A referencia separa
  // `zoomCuts` (o salto duro de plano NA emenda, base ciclando de 1,10 a 1,22)
  // de `zoomAuto` (o push-in lento DENTRO do trecho, +0,04). Da para querer um
  // sem o outro: push-in sozinho e movimento discreto sem trocar de plano; salto
  // sozinho e ritmo de corte sem deriva. Antes os dois vinham juntos num toggle.
  const [zoomCuts, setZoomCuts] = useState(true);
  const [zoomAuto, setZoomAuto] = useState(true);
  const [tracking, setTracking] = useState(true);
  const [cutFlash, setCutFlash] = useState(true);
  const [cutClick, setCutClick] = useState(true);
  // Tela dividida: quantas inserções de imagem gerada por IA o vídeo pode ter.
  // É um TETO, e a IA pode entregar menos — um vídeo sem momento que peça imagem
  // sai com nenhuma, e isso é o resultado certo. Por isso o padrão é 0 (opt-in) e
  // por isso a cobrança é por inserção ENTREGUE, feita no fim do job.
  const [imageInsertCount, setImageInsertCount] = useState(0);
  const [questionTop, setQuestionTop] = useState('O que você me perguntaria?');
  const [questionBottom, setQuestionBottom] = useState('');
  const [objective, setObjective] = useState('');
  const [smartReframe, setSmartReframe] = useState(true);
  // Trilha de fundo escolhida por IA. Ligada por padrao, volume 10% — que e o
  // patamar em que a musica da ritmo sem disputar com a locucao.
  const [addMusic, setAddMusic] = useState(true);
  const [musicVolume, setMusicVolume] = useState(10);
  // Elegibilidade da empresa para trilha do Instagram. `null` = ainda checando.
  // Sem isso a pessoa marcava a caixa, esperava a edicao inteira, e o video saia
  // sem musica sem nenhuma explicacao na tela — so no log do servidor.
  const [musicEligibility, setMusicEligibility] = useState<{ eligible: boolean; reason?: string | null } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getInstagramMusicEligibility()
      .then(r => {
        if (cancelled) return;
        setMusicEligibility({ eligible: r.eligible, reason: r.reason });
        if (!r.eligible) setAddMusic(false);
      })
      .catch(() => { if (!cancelled) setMusicEligibility({ eligible: true, reason: null }); });
    return () => { cancelled = true; };
  }, []);
  const [editorTarget, setEditorTarget] = useState<{ jobId: string; target: string; title: string } | null>(null);
  const [edvidTarget, setEdvidTarget] = useState<{ jobId: string; tab: EdvidTab; title: string } | null>(null);
  // Passo a passo das escolhas (0 = fechado). Ver o comentario junto do render.
  const [etapaAberta, setEtapaAberta] = useState<number>(0);
  const tituloEtapaRef = useRef<HTMLHeadingElement | null>(null);
  const fundoPressionadoRef = useRef(false);
  useEffect(() => { if (etapaAberta) tituloEtapaRef.current?.focus(); }, [etapaAberta]);
  useEffect(() => {
    if (!etapaAberta) return;
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') setEtapaAberta(0); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [etapaAberta]);
  const { request: requestEditConfirm, modal: editConfirmModal } = useCreditConfirm();
  const { refetch: refetchCredits } = useCredits();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadJobs = async (silent = false) => {
    try {
      const res = await api.listVideoEditorJobs(brand?.id, activeTab);
      setJobs(res.jobs || []);
    } catch (err: any) {
      if (!silent) console.error('Erro ao carregar jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
    pollingRef.current = setInterval(() => {
      loadJobs(true);
    }, 4000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [brand?.id, activeTab]);

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Barra arquivos acima do teto ANTES de enviar: o servidor recusaria com 413
  // depois de o upload inteiro ter subido.
  const acceptFiles = (incoming: File[]) => {
    const tooBig = incoming.filter(f => f.size > VIDEO_EDITOR_MAX_FILE_MB * 1024 * 1024);
    if (tooBig.length) {
      toast.error(`${tooBig.map(f => `"${f.name}" (${(f.size / 1024 / 1024).toFixed(0)} MB)`).join(', ')} passa do limite de ${VIDEO_EDITOR_MAX_FILE_MB} MB por vídeo.`);
    }
    const ok = incoming.filter(f => !tooBig.includes(f));
    if (ok.length) setFiles(prev => [...prev, ...ok].slice(0, 10));
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFiles(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!files.length) return toast.error('Selecione pelo menos 1 vídeo.');
    // Custo na cara do usuário antes de gastar: o upload só começa depois do OK.
    requestEditConfirm({
      title: activeTab === 'viral_cuts' ? 'Gerar cortes virais com IA' : 'Editar vídeo com IA',
      // A inserção de imagem entra como LINHA SEPARADA e pelo TETO: é o pior caso
      // do custo, que é o que a pessoa precisa ver antes de confirmar. A cobrança
      // real conta só o que entrar no vídeo, e a nota diz isso.
      items: imageInsertCount > 0 && activeTab !== 'viral_cuts'
        ? [{ featureKey: EDIT_FEATURE_KEY[activeTab], qty: 1 }, { featureKey: 'video_image_insert', qty: imageInsertCount }]
        : [{ featureKey: EDIT_FEATURE_KEY[activeTab], qty: 1 }],
      note: [
        activeTab === 'viral_cuts'
          ? `Cobrança única por geração, independente de quantos cortes saírem (${clipCount} previstos). Se a edição falhar, o crédito é devolvido.`
          : 'Cobrança única por edição, independente de quantos vídeos você enviar. Se a edição falhar, o crédito é devolvido.',
        imageInsertCount > 0 && activeTab !== 'viral_cuts'
          ? `As inserções de imagem são cobradas só pelas que ENTRAREM no vídeo (até ${imageInsertCount}). Se a IA não achar momento que peça imagem, ela entrega menos — ou nenhuma — e você paga menos.`
          : '',
      ].filter(Boolean).join(' '),
      confirmLabel: activeTab === 'viral_cuts' ? 'Gerar cortes' : 'Editar vídeo',
      onConfirm: () => { void startUpload(); },
    });
  };

  const startUpload = async () => {
    setUploading(true);
    try {
      // Um vídeo por requisição, em série. Antes iam os 9 numa única multipart:
      // um arquivo acima do limite derrubava o envio inteiro, sem progresso.
      const uploaded: { url: string; filename: string }[] = [];
      for (let i = 0; i < files.length; i++) {
        setUploadProgress({ index: i, fraction: 0 });
        const res = await api.uploadVideoEditorSource(
          files[i],
          { mode: activeTab, index: i },
          fraction => setUploadProgress({ index: i, fraction }),
        );
        uploaded.push({ url: res.upload.url, filename: res.upload.filename });
      }
      setUploadProgress(null);

      const edvidJob = VIDEO_EDITOR_ENGINE === 'edvid' && activeTab === 'editor';
      const payload: Record<string, unknown> = edvidJob ? {
        // Motor edvid: só direção/duração/objetivo. O estilo vem depois, no editor.
        videos: uploaded,
        mode: activeTab,
        engine: 'edvid',
        options: { engine: 'edvid' },
        ...(objective ? { objective } : {}),
        smartReframe,
      } : {
        videos: uploaded,
        // Derivado do card escolhido. Não multiplica mais por `!!brand`: só o
        // 'hook' precisa de marca, e esse card já vem desabilitado sem ela.
        addOverlays: overlayStyle !== 'none',
        mode: activeTab,
        clipCount,
        ...(objective ? { objective } : {}),
        overlayStyle,
        // Enviados sempre, nao so no modo legenda: o backend ja ignora o que nao se
        // aplica e um campo ausente obrigaria ele a repetir o padrao do front.
        captionStyle,
        headlineStyle,
        zoomCuts,
        zoomAuto,
        tracking,
        cutFlash,
        cutClick,
        imageInsertCount: activeTab === 'viral_cuts' ? 0 : imageInsertCount,
        smartReframe,
        addMusic: addMusic && musicEligibility?.eligible !== false,
        musicVolume: musicVolume / 100,
      };
      if (brand) payload.brandId = brand.id;
      if (idea.trim()) payload.idea = idea.trim();
      if (targetDuration > 0 && activeTab !== 'viral_cuts') payload.maxDurationSeconds = targetDuration;
      if (overlayStyle === 'question_box') {
        payload.questionBoxTop = questionTop.trim();
        payload.questionBoxBottom = questionBottom.trim();
      }

      const res = await api.startVideoEditorJob(payload);
      setJobs(prev => [res.job, ...prev]);
      setFiles([]);
      // O débito acontece na criação do job: atualiza o saldo do header na hora.
      void refetchCredits();
      toast.success(`${files.length > 1 ? `${files.length} vídeos enviados` : 'Vídeo enviado'}! Processando...`);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao iniciar edição.');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = async (jobId: string) => {
    if (!window.confirm('Excluir esta edição e seus arquivos-fonte? Esta ação não pode ser desfeita.')) return;
    try {
      await api.deleteVideoEditorJob(jobId);
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (err: any) {
      toast.error(err.message || 'Erro ao remover.');
    }
  };

  const totalMB = files.reduce((s, f) => s + f.size, 0) / 1024 / 1024;

  // ── Passo a passo ───────────────────────────────────────────────────────────
  //
  // A tela de criação mostrava TUDO de uma vez: direção, duração, overlay, legenda,
  // elementos, tela dividida, música, custo — um formulário longo antes mesmo de
  // escolher o vídeo. Agora ela segue o mesmo desenho do "Gerar conteúdo" do brief:
  // na tela fica só o envio do vídeo e o botão; as escolhas vêm depois, uma etapa
  // por vez, com "Passo N de M" e um Voltar que não perde nada (as escolhas moram
  // no estado deste componente, não no modal).
  //
  // Edição e Cortes Virais usam as MESMAS etapas e as mesmas ferramentas; só o que
  // é próprio de cada modo muda dentro delas (duração x quantidade de cortes, tela
  // dividida só na edição — ver o backend).
  type EtapaId = 'direcao' | 'overlay' | 'legenda' | 'elementos' | 'musica';
  const isViral = activeTab === 'viral_cuts';
  // Motor edvid (só na Edição; Cortes Virais seguem no fluxo antigo por enquanto):
  // o passo a passo fica só com a direção. Estilo, elementos e música são escolhidos
  // DEPOIS do corte, na aba Estilo do editor, olhando o próprio vídeo.
  const edvidFlow = VIDEO_EDITOR_ENGINE === 'edvid' && !isViral;
  const etapas: EtapaId[] = edvidFlow
    ? ['direcao']
    : ['direcao', 'overlay', ...(overlayStyle === 'caption' ? ['legenda' as const] : []), 'elementos', 'musica'];
  const etapaAtual = etapaAberta > 0 ? etapas[Math.min(etapaAberta, etapas.length) - 1] : null;

  const ETAPA_INFO: Record<EtapaId, { titulo: string; sub: string }> = {
    direcao: {
      titulo: isViral ? 'Direção dos cortes' : 'Direção da edição',
      sub: isViral ? 'Quantos cortes, com que objetivo e o que a IA deve priorizar.' : 'O que a IA deve priorizar, o objetivo e a duração.',
    },
    overlay: { titulo: 'Overlay do vídeo', sub: 'O desenho do texto na tela. Escolha olhando: é o layout do vídeo inteiro.' },
    legenda: { titulo: 'Estilo da legenda e do gancho', sub: 'Como a legenda e o gancho aparecem na tela.' },
    elementos: { titulo: 'Elementos de edição', sub: 'O ritmo do corte. São os mesmos do passo a passo do brief.' },
    musica: { titulo: 'Música e resumo', sub: 'Trilha de fundo e o custo antes de enviar.' },
  };

  const abrirPassoAPasso = (e: React.FormEvent) => {
    e.preventDefault();
    if (!files.length) return toast.error('Selecione pelo menos 1 vídeo.');
    setEtapaAberta(1);
  };
  const avancarEtapa = (e: React.FormEvent) => {
    if (etapaAberta < etapas.length) { setEtapaAberta(etapaAberta + 1); return; }
    setEtapaAberta(0);
    handleUpload(e);
  };

  const campoLabel = 'mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-400';
  const campoInput = 'w-full rounded-xl border border-slate-700 bg-slate-800 px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 outline-none focus:border-indigo-500';

  return (
    <div className="space-y-6">
      {editConfirmModal}
      {editorTarget && (
        <VideoProjectEditor
          jobId={editorTarget.jobId}
          target={editorTarget.target}
          title={editorTarget.title}
          onClose={() => setEditorTarget(null)}
        />
      )}
      {edvidTarget && (
        <EdvidEditor
          jobId={edvidTarget.jobId}
          title={edvidTarget.title}
          initialTab={edvidTarget.tab}
          brandAccent={accentColor !== '#ff5200' ? accentColor : null}
          onClose={() => { setEdvidTarget(null); void loadJobs(true); }}
        />
      )}
      {/* Header */}
      <div>
        <h2 className="text-2xl font-black text-slate-900">Edição de Vídeo</h2>
        <p className="mt-1 text-sm text-slate-500 max-w-2xl">
          Envie vídeos brutos para editar um vídeo final ou gerar cortes virais prontos para Reels, TikTok e Shorts.
        </p>
      </div>

      <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1 shadow-sm">
        {[
          { id: 'editor' as const, label: 'Edição de Vídeo' },
          { id: 'viral_cuts' as const, label: 'Cortes Virais' },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => { setActiveTab(tab.id); setLoading(true); }}
            className={`rounded-xl px-5 py-2.5 text-sm font-bold transition-colors ${activeTab === tab.id ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Envio: só o vídeo e o botão. As escolhas vêm no passo a passo. */}
      <form onSubmit={abrirPassoAPasso} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm space-y-5">
        <div
          onClick={() => { if (!uploading) fileInputRef.current?.click(); }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            if (uploading) return;
            acceptFiles(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('video/')));
          }}
          className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
        >
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-100 text-2xl">🎬</div>
          <p className="font-semibold text-slate-700">Clique ou arraste os vídeos aqui</p>
          <p className="mt-1 text-xs text-slate-400">MP4, MOV, AVI, MKV, WEBM · Até 10 arquivos, {VIDEO_EDITOR_MAX_FILE_MB / 1024} GB cada</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            multiple
            className="hidden"
            onChange={handleFilePick}
          />
        </div>

        {files.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {files.length} vídeo{files.length !== 1 ? 's' : ''} selecionado{files.length !== 1 ? 's' : ''} · {totalMB.toFixed(1)} MB total
            </p>
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-xs font-bold text-indigo-600">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{f.name}</p>
                  <p className="text-xs text-slate-400">
                    {(f.size / 1024 / 1024).toFixed(1)} MB
                    {uploadProgress && uploadProgress.index > i ? ' · enviado' : ''}
                    {uploadProgress && uploadProgress.index === i ? ` · enviando ${Math.round(uploadProgress.fraction * 100)}%` : ''}
                  </p>
                  {uploadProgress && uploadProgress.index === i && (
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                      <div className="h-full bg-indigo-500 transition-all" style={{ width: `${Math.round(uploadProgress.fraction * 100)}%` }} />
                    </div>
                  )}
                </div>
                {!uploading && (
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-xs text-rose-400 hover:text-rose-600 px-2 py-1 rounded-lg hover:bg-rose-50"
                  >
                    Remover
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={uploading || !files.length}
          className="w-full rounded-xl bg-indigo-600 py-3.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {uploading
            ? (uploadProgress
              ? `Enviando vídeo ${uploadProgress.index + 1}/${files.length} · ${Math.round(uploadProgress.fraction * 100)}%`
              : 'Iniciando edição...')
            : files.length === 0
              ? 'Selecione pelo menos 1 vídeo'
              : isViral ? 'Continuar para os cortes →' : 'Continuar para a edição →'}
        </button>
      </form>

      {etapaAtual && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          // Fecha só se o clique COMEÇOU e TERMINOU no fundo. Com onClick puro,
          // selecionar texto num campo e soltar o mouse fora do modal disparava o
          // click no fundo e fechava o passo a passo.
          onMouseDown={e => { fundoPressionadoRef.current = e.target === e.currentTarget; }}
          onClick={e => { if (fundoPressionadoRef.current && e.target === e.currentTarget) setEtapaAberta(0); fundoPressionadoRef.current = false; }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="video-editor-etapa-titulo"
        >
          <div
            className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <style>{CAPTION_DEMO_CSS}</style>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-5">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Passo {etapaAberta} de {etapas.length}</p>
                  <h2 id="video-editor-etapa-titulo" ref={tituloEtapaRef} tabIndex={-1} className="mt-1 text-base font-bold text-slate-100 outline-none">
                    {ETAPA_INFO[etapaAtual].titulo}
                  </h2>
                  <p className="mt-0.5 text-[11px] text-slate-500">{ETAPA_INFO[etapaAtual].sub}</p>
                </div>
                <div className="mt-1 flex shrink-0 items-center gap-1.5" aria-hidden="true">
                  {etapas.map((_, i) => (
                    <span key={i} className={`h-1.5 rounded-full transition-all ${i + 1 === etapaAberta ? 'w-5 bg-indigo-400' : i + 1 < etapaAberta ? 'w-1.5 bg-indigo-700' : 'w-1.5 bg-slate-700'}`} />
                  ))}
                </div>
              </div>

              {etapaAtual === 'direcao' && (
                <div className="space-y-4">
                  {isViral && (
                    <div>
                      <label className={campoLabel}>Quantidade de cortes</label>
                      <select value={clipCount} onChange={e => setClipCount(Number(e.target.value))} className={campoInput}>
                        {[3, 5, 8, 10, 12].map(n => <option key={n} value={n}>{n} cortes</option>)}
                      </select>
                      <p className="mt-1 text-[11px] text-slate-500">Cada corte sai com ~30s (entre 15s e 60s).</p>
                    </div>
                  )}
                  <div>
                    <label className={campoLabel}>
                      {isViral ? 'Direção dos cortes' : 'Ideia ou direção criativa'}
                      <span className="ml-1.5 font-normal normal-case text-slate-500">(opcional)</span>
                    </label>
                    <textarea
                      value={idea}
                      onChange={e => setIdea(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      placeholder="Ex: comece pelo momento mais impactante, mostre o resultado antes de explicar o processo..."
                      className={`${campoInput} resize-none`}
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      A direção vem antes das preferências da IA. Só as regras técnicas (erro de fala, silêncio, trecho fora do tema) ficam acima dela.
                    </p>
                  </div>
                  <div>
                    <label className={campoLabel}>Objetivo</label>
                    <select value={objective} onChange={e => setObjective(e.target.value)} className={campoInput}>
                      <option value="">Sem objetivo específico</option>
                      <option value="vendas">Vendas</option>
                      <option value="autoridade">Autoridade</option>
                      <option value="educativo">Educativo</option>
                      <option value="engajamento">Engajamento</option>
                    </select>
                    <p className="mt-1 text-[11px] text-slate-500">Decide quais trechos entram e em que ordem.</p>
                  </div>
                  {!isViral && (
                    <div>
                      <label className={campoLabel}>Duração do vídeo final</label>
                      <select value={targetDuration} onChange={e => setTargetDuration(Number(e.target.value))} className={campoInput}>
                        <option value={0}>Automático (proporcional ao material)</option>
                        <option value={30}>Até 30 segundos</option>
                        <option value={45}>Até 45 segundos</option>
                        <option value={60}>Até 1 minuto</option>
                        <option value={90}>Até 1min30</option>
                        <option value={120}>Até 2 minutos</option>
                        <option value={180}>Até 3 minutos</option>
                      </select>
                      <p className="mt-1 text-[11px] text-slate-500">O piso é 75% do valor escolhido. No automático, o alvo é ~60% do material.</p>
                    </div>
                  )}
                  <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${smartReframe ? 'border-indigo-400 bg-indigo-500/15' : 'border-slate-700 bg-white/[0.03]'}`}>
                    <input type="checkbox" checked={smartReframe} onChange={e => setSmartReframe(e.target.checked)} className="mt-0.5 h-4 w-4 rounded text-indigo-600" />
                    <div>
                      <p className="text-sm font-semibold text-slate-100">Enquadrar apresentador em 9:16</p>
                      <p className="mt-0.5 text-[11px] text-slate-400">Em vídeo largo (16:9), a IA acha onde a pessoa está e recorta o vertical mantendo rosto e tronco no quadro.</p>
                    </div>
                  </label>
                  {edvidFlow && (
                    <>
                      <p className="rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-3 text-[11px] leading-relaxed text-indigo-100">
                        Primeiro sai o <strong>corte</strong> para você aprovar. Depois, na aba <strong>Estilo</strong> do editor, você escolhe legenda, gancho, cor, elementos e música vendo a prévia sobre o próprio vídeo.
                      </p>
                      <CreditCostNotice
                        items={[{ featureKey: EDIT_FEATURE_KEY[activeTab], qty: 1 }]}
                        note="Cobrado uma vez por edição, não por vídeo enviado."
                      />
                    </>
                  )}
                </div>
              )}

              {etapaAtual === 'overlay' && (
                <div>
                  <OverlayStyleCards value={overlayStyle} onChange={setOverlayStyle} brand={brand} tema="escuro" colunas="grid-cols-2 sm:grid-cols-4" />
                  {overlayStyle !== 'question_box' ? (
                    <OverlayStyleHelp value={overlayStyle} className="mt-3 text-[11px] leading-relaxed text-slate-400" />
                  ) : (
                    <div className="mt-3 space-y-3">
                      <p className="text-[11px] text-slate-400">
                        A caixinha substitui todos os overlays e fica na tela do começo ao fim{isViral ? ', em cada corte' : ''}. A IA edita para <strong>responder</strong> a pergunta.
                      </p>
                      <div>
                        <label className={campoLabel}>Texto de cima (a chamada)</label>
                        <input value={questionTop} onChange={e => setQuestionTop(e.target.value)} maxLength={160} placeholder="Eu e você tomando um café. O que você me perguntaria?" className={campoInput} />
                      </div>
                      <div>
                        <label className={campoLabel}>A pergunta (o vídeo responde ela)</label>
                        <input value={questionBottom} onChange={e => setQuestionBottom(e.target.value)} maxLength={200} placeholder="Como ter ideias de caixinhas pra postar?" className={campoInput} />
                        {!questionBottom.trim() && (
                          <p className="mt-1 text-[11px] text-amber-400">Sem a pergunta a caixinha não é aplicada — o vídeo sai com o hook normal.</p>
                        )}
                      </div>
                      <div className="mx-auto max-w-[280px] overflow-hidden rounded-xl shadow-sm">
                        <div className="bg-[#1C1C1E] px-3 py-2.5 text-center text-[11px] font-semibold leading-snug text-white">
                          {questionTop.trim() || 'O que você me perguntaria?'}
                        </div>
                        <div className="bg-white px-3 py-3 text-center text-[13px] leading-snug text-[#1C1C1E]">
                          {questionBottom.trim() || 'Escreva a pergunta aqui'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {etapaAtual === 'legenda' && (
                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Estilo de legenda</p>
                    <CaptionStyleCards value={captionStyle} onChange={setCaptionStyle} tema="escuro" colunas="grid-cols-2" altura="h-12" />
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Estilo do gancho</p>
                    <HeadlineStyleCards value={headlineStyle} onChange={setHeadlineStyle} accent={accentColor} tema="escuro" colunas="grid-cols-2" altura="h-12" />
                    <p className="mt-1.5 text-[11px] text-slate-500">O gancho aparece nos primeiros segundos e depois sai; a legenda continua.</p>
                  </div>
                </div>
              )}

              {etapaAtual === 'elementos' && (
                <div className="space-y-4">
                  {/* Os MESMOS cartões do passo a passo do brief e do editor manual. */}
                  <EditElementsPanel
                    value={{ zoomCuts, zoomAuto, tracking, cutFlash, cutClick }}
                    onChange={patch => {
                      if (patch.zoomCuts !== undefined) setZoomCuts(patch.zoomCuts);
                      if (patch.zoomAuto !== undefined) setZoomAuto(patch.zoomAuto);
                      if (patch.tracking !== undefined) setTracking(patch.tracking);
                      if (patch.cutFlash !== undefined) setCutFlash(patch.cutFlash);
                      if (patch.cutClick !== undefined) setCutClick(patch.cutClick);
                    }}
                    tema="escuro"
                    colunas="grid-cols-3"
                    rotulo="O que aplicar no corte"
                  />
                  {/* Tela dividida só na edição: nos cortes virais as inserções
                      entrariam só no compilado, não nos cortes publicados. */}
                  {!isViral && overlayStyle !== 'question_box' && (
                    <div className="rounded-xl border border-slate-700 bg-white/[0.03] p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <label htmlFor="image-insert-count" className={campoLabel}>Inserções de imagem (tela dividida)</label>
                          <p className="text-[11px] leading-relaxed text-slate-400">
                            A IA gera uma imagem do que está sendo falado e divide a tela. Ela entrega menos que o teto quando o vídeo não pede imagem.
                          </p>
                        </div>
                        <select
                          id="image-insert-count"
                          value={imageInsertCount}
                          onChange={e => setImageInsertCount(Number(e.target.value))}
                          className="shrink-0 rounded-xl border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
                        >
                          <option value={0}>Nenhuma</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>até {n}</option>)}
                        </select>
                      </div>
                      {imageInsertCount > 0 && (
                        <p className="mt-2 text-[11px] leading-snug text-amber-300">
                          Cobrado só pelas inserções que entrarem no vídeo, não pelo teto.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {etapaAtual === 'musica' && (
                <div className="space-y-4">
                  <div className={`rounded-xl border p-3 ${musicEligibility && !musicEligibility.eligible ? 'border-amber-400/30 bg-amber-500/10' : addMusic ? 'border-emerald-400/40 bg-emerald-500/10' : 'border-slate-700 bg-white/[0.03]'}`}>
                    <label className={`flex items-start gap-3 ${musicEligibility && !musicEligibility.eligible ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={addMusic && musicEligibility?.eligible !== false}
                        disabled={musicEligibility === null || !musicEligibility.eligible}
                        onChange={e => setAddMusic(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded text-emerald-600 disabled:opacity-40"
                      />
                      <div>
                        <p className="text-sm font-semibold text-slate-100">Música de fundo escolhida por IA</p>
                        {musicEligibility === null ? (
                          <p className="mt-0.5 text-[11px] text-slate-400">Verificando se há conta do Instagram elegível...</p>
                        ) : musicEligibility.eligible ? (
                          <p className="mt-0.5 text-[11px] text-slate-400">A IA escolhe uma música em alta no Instagram que combine com o vídeo. Dá para trocar ou remover no editor.</p>
                        ) : (
                          <p className="mt-0.5 text-[11px] text-amber-300">Indisponível: {musicEligibility.reason}</p>
                        )}
                      </div>
                    </label>
                    {addMusic && musicEligibility?.eligible !== false && (
                      <div className="mt-3 pl-7">
                        <label className={campoLabel}>Volume da música: {musicVolume}%</label>
                        <input type="range" min={1} max={100} value={musicVolume} onChange={e => setMusicVolume(Number(e.target.value))} className="w-full" />
                        <p className="mt-1 text-[11px] text-slate-500">A faixa é anexada na publicação do Instagram — o arquivo baixado sai sem ela.</p>
                      </div>
                    )}
                  </div>
                  <CreditCostNotice
                    items={imageInsertCount > 0 && !isViral
                      ? [{ featureKey: EDIT_FEATURE_KEY[activeTab], qty: 1 }, { featureKey: 'video_image_insert', qty: imageInsertCount }]
                      : [{ featureKey: EDIT_FEATURE_KEY[activeTab], qty: 1 }]}
                    note={isViral ? 'Cobrado uma vez por geração, não por corte gerado.' : 'Cobrado uma vez por edição, não por vídeo enviado.'}
                  />
                </div>
              )}

              <div className="sticky bottom-0 -mx-6 mt-4 flex items-center justify-between gap-3 border-t border-slate-800 bg-slate-900 px-6 py-3">
                <button
                  type="button"
                  onClick={() => setEtapaAberta(etapaAberta <= 1 ? 0 : etapaAberta - 1)}
                  className="rounded-xl border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-slate-800"
                >
                  {etapaAberta <= 1 ? 'Cancelar' : 'Voltar'}
                </button>
                <button
                  type="button"
                  onClick={avancarEtapa}
                  className="rounded-xl bg-indigo-700 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-600"
                >
                  {etapaAberta >= etapas.length
                    ? (isViral ? `Gerar ${clipCount} cortes →` : 'Editar vídeo →')
                    : 'Continuar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Jobs */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-800">
            Histórico de edições
            {jobs.length > 0 && <span className="ml-2 text-slate-400 font-normal text-sm">({jobs.length})</span>}
          </h3>
          <button onClick={() => loadJobs()} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50">
            Atualizar
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400">Carregando...</p>
        ) : jobs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
            <p className="text-2xl mb-2">🎬</p>
            <p className="text-sm font-medium text-slate-500">Nenhuma edição ainda</p>
            <p className="text-xs text-slate-400 mt-1">Envie seus vídeos brutos e deixe a IA fazer a magia.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                onDelete={() => handleDelete(job.id)}
                onOpenEditor={(target, editorTitle) => setEditorTarget({ jobId: job.id, target, title: editorTitle })}
                onOpenEdvid={(tab, editorTitle) => setEdvidTarget({ jobId: job.id, tab, title: editorTitle })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
